import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  getEquipmentBrandV2,
  getEquipmentCategory,
  getEquipmentsV2,
} from "../../api";

const categoryIDMap = {
  All: NaN,
  Mobile: 1,
  Basic: 2,
  Smart: 3,
  POS: 4,
  Gateway: 5,
  "Add-on": 6,
};

const initialState = {
  brands: [],
  brandsLoading: true,
  activeBrand: null,
  brandFilter: {
    searchTerm: "",
    page: 1,
  },
  addBrandModalOpen: false,
  deleteBrandModalOpen: false,
  addEquipmentModalOpen: false,
  addEquipmentCategoryModalOpen: false,
  models: [],
  modelsLoading: true,
  allModels: [],
  allModelsLoading: true,
  activeModel: null,
  activeModelId: null,
  modelFilter: {
    searchTerm: "",
    page: 1,
  },
  categories: [],
  categoriesLoading: true,
  selectedCategory: "All",
  activeCategory: null,
  selectedModelIds: [],
  deleteModelModalOpen: false,
};

const useEquipmentStore = create(
  devtools((set) => ({
    ...initialState,
    setBrandsLoading: (state) => set({ brandsLoading: state }),
    setActiveBrand: (brand) => set({ activeBrand: brand }),
    setBrandFilter: (filterItems) =>
      set((state) => ({
        brandFilter: {
          ...state.brandFilter,
          ...filterItems,
        },
      })),
    setModelFilter: (filterItems) =>
      set((state) => ({
        modelFilter: {
          ...state.modelFilter,
          ...filterItems,
        },
      })),
    setModelsLoading: (state) => set({ modelsLoading: state }),
    setActiveModelId: (id) => set({ activeModelId: id }),
    setActiveModel: (model) => set({ activeModel: model }),
    setDeleteModelModalOpen: (state) => set({ deleteModelModalOpen: state }),
    setAddBrandModalOpen: (state) => set({ addBrandModalOpen: state }),
    setAddEquipmentModalOpen: (state) => set({ addEquipmentModalOpen: state }),
    setAddEquipmentCategoryModalOpen: (state) =>
      set({ addEquipmentCategoryModalOpen: state }),
    setDeleteBrandModalOpen: (state) => set({ deleteBrandModalOpen: state }),
    // ToDo: selected category is used for filtering models
    // move it to modelFilter
    setSelectedCategory: (category) => set({ selectedCategory: category }),
    addToSelectedModelIds: (id) =>
      set((state) => ({
        selectedModelIds: [...state.selectedModelIds, id],
      })),
    removeFromSelectedModelIds: (idToRemove) =>
      set((state) => ({
        selectedModelIds: state.selectedModelIds.filter(
          (id) => id !== idToRemove,
        ),
      })),
    clearSelectedModelIds: () => set({ selectedModelIds: [] }),
    setActiveCategory: (category) => set({ activeCategory: category }),
    reset: () => set(initialState),
    resetBrands: () =>
      set({
        brands: initialState.brands,
        brandFilter: initialState.brandFilter,
        brandsLoading: initialState.brandsLoading,
      }),
    resetModels: () =>
      set({
        models: initialState.models,
        modelFilter: initialState.modelFilter,
        modelsLoading: initialState.modelsLoading,
      }),
    resetCategories: () =>
      set({
        categories: initialState.categories,
        categoriesLoading: initialState.categoriesLoading,
        activeCategory: null,
      }),
  })),
);

export const equipmentDropdownOptions = (history, isV2Enabled) => [
  {
    text: "Add brand",
    onClick: () => {
      useEquipmentStore.getState().setAddBrandModalOpen(true);
    },
  },
  {
    text: "Add equipment",
    onClick: () => {
      useEquipmentStore.getState().setAddEquipmentModalOpen(true);
    },
  },
  {
    text: "Add equipment category",
    onClick: () =>
      useEquipmentStore.getState().setAddEquipmentCategoryModalOpen(true),
  },
  {
    text: "View equipment categories",
    onClick: () =>
      history.push(
        isV2Enabled ? "/equipments/categories" : "/v2/equipments/categories",
      ),
  },
];

export const modelDropdownOptions = (model) => [
  {
    text: "Edit",
    onClick: () => {
      useEquipmentStore.getState().setActiveModel(model);
      useEquipmentStore.getState().setActiveModelId(model?.id);
      useEquipmentStore.getState().setAddEquipmentModalOpen(true);
    },
  },
  {
    text: "Delete",
    type: "danger",
    onClick: () => {
      useEquipmentStore.getState().setActiveModel(model);
      useEquipmentStore.getState().setActiveModelId(model?.id);
      useEquipmentStore.getState().setDeleteModelModalOpen(true);
    },
  },
];

export const fetchBrands = async (abortController) => {
  useEquipmentStore.setState({ brandsLoading: true });
  try {
    const response = await getEquipmentBrandV2(
      {
        all: "all",
        search_keyword: useEquipmentStore.getState().brandFilter.searchTerm,
      },
      abortController,
    );

    if (!response.status) {
      useEquipmentStore.setState({ brandsLoading: false });
      return;
    }

    useEquipmentStore.setState({ brands: response.data, brandsLoading: false });
  } catch (error) {
    console.error(error);
  }
};

export const debouncedFetchBrands = (abortController) => {
  return setTimeout(() => {
    fetchBrands(abortController);
  }, 300);
};

export const fetchModels = async (
  brandId,
  abortController,
  allModels = false,
) => {
  const stateToUse = allModels ? "allModels" : "models";

  useEquipmentStore.setState({
    [stateToUse + "Loading"]: true,
  });

  try {
    const response = await getEquipmentsV2(
      {
        brand_id: brandId,
        category_id:
          categoryIDMap[useEquipmentStore.getState().selectedCategory],
        noCategorySplit: true,
        withCategory: true,
        withBrand: true,
        search_keyword: useEquipmentStore.getState().modelFilter.searchTerm,
      },
      abortController,
    );

    if (!response.status) {
      console.error(response);
      return;
    }

    console.log(response.data);

    useEquipmentStore.setState({
      [stateToUse]: response.data,
      [stateToUse + "Loading"]: false,
    });
  } catch (error) {
    console.error(error);
  }
};

export const debouncedFetchModels = (brandId, abortController) => {
  return setTimeout(() => {
    fetchModels(brandId, abortController);
  }, 300);
};

export const fetchCategories = async (abortController) => {
  try {
    const response = await getEquipmentCategory(abortController);
    if (!response.status) {
      console.error(response);
      return;
    }
    useEquipmentStore.setState({
      categories: response.data,
      categoriesLoading: false,
    });
  } catch (error) {
    console.error(error);
  }
};

export default useEquipmentStore;
